import { useState } from "react";
import { Slider } from "@/components/primitives/slider";
import { secondsToMMSS } from "@/lib/utils";

type Props = {
  currentTime: number;
  duration: number;
  /** Expanded = interactive scrub row; collapsed = passive thin progress line. */
  expanded: boolean;
  /** Height of the controls bar, so the expanded row docks on top of it. */
  bottomInset: number;
  onSeek: (time: number) => void;
  /** Any interaction — keeps the auto-hide timer alive while scrubbing. */
  onInteract: () => void;
};

/**
 * The video progress strip for the immersive viewer UI.
 *
 * Collapsed (controls hidden): a thin, non-interactive progress line pinned to
 * the very bottom of the screen. Expanded (controls shown): it docks on top of
 * the controls bar and becomes a seekable slider with a time label.
 *
 * Positioned as a SIBLING of the zoom layer — fixed to the screen, never to
 * the video frame — so it neither scales nor pans with the media. The Radix
 * slider renders role="slider", which the viewer's swipe/keyboard guards
 * already treat as interactive (no carousel navigation from a scrub drag).
 */
export default function VideoScrubBar({
  currentTime,
  duration,
  expanded,
  bottomInset,
  onSeek,
  onInteract,
}: Props) {
  // Local value while dragging, so the thumb tracks the finger; the actual
  // seek fires on commit (release) — a live-seek per move would thrash range
  // requests on a streamed source.
  const [dragValue, setDragValue] = useState<number | null>(null);
  // Clamp: before metadata loads `duration` is 0 (so max falls back to 1) while
  // currentTime can already be past it — Radix rejects value > max.
  const max = duration > 0 ? duration : 1;
  const value = Math.min(Math.max(dragValue ?? currentTime, 0), max);
  const progress = duration > 0 ? value / duration : 0;

  return (
    <div
      data-no-swipe=""
      className="absolute inset-x-0 z-10 transition-all duration-200"
      style={{ bottom: expanded ? bottomInset : 0 }}
    >
      {expanded ? (
        // Same frosted backdrop as the controls bar beneath it, so the two
        // read as one surface.
        <div className="flex items-center gap-3 bg-white/90 px-4 py-2 backdrop-blur-md dark:bg-black/90">
          <Slider
            aria-label="Seek"
            className="flex-1"
            value={[value]}
            max={max}
            step={0.1}
            onValueChange={(v) => {
              setDragValue(v[0]);
              onInteract();
            }}
            onValueCommit={(v) => {
              onSeek(v[0]);
              setDragValue(null);
              onInteract();
            }}
            // Radix Slider never handles pointercancel: a cancelled touch
            // (notification-center pull, incoming call) fires no commit, which
            // would leave dragValue pinned and the bar frozen at the drag
            // position. lostpointercapture covers the capture-teardown path —
            // after a normal release it fires AFTER pointerup, when commit
            // already cleared, so the extra reset is harmless.
            onPointerCancel={() => setDragValue(null)}
            onLostPointerCapture={() => setDragValue(null)}
          />
          <span className="shrink-0 text-xs tabular-nums opacity-80">
            {secondsToMMSS(Math.floor(value))} /{" "}
            {secondsToMMSS(Math.floor(duration))}
          </span>
        </div>
      ) : (
        <div
          data-testid="video-progress-line"
          className="pointer-events-none h-[3px] w-full bg-white/20"
        >
          <div
            className="h-full bg-primary"
            style={{ width: `${progress * 100}%` }}
          />
        </div>
      )}
    </div>
  );
}
